import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Space, Popconfirm, Tag, DatePicker, Input, Select, Modal, message, Row, Col, Divider, Dropdown, Tooltip } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EyeOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, FilterOutlined, ClearOutlined, DownloadOutlined, DownOutlined, BankOutlined, LinkOutlined } from "@ant-design/icons";
import * as adminactions from "store/actions/AuthActions";
import dayjs from "dayjs";
import axios from "axios";
import debounce from "lodash/debounce";
import BankTransactionSelectModal from "../../components/Common/BankTransactionSelectModal";
import useUrlFilters from "hooks/useUrlFilters";

const { Option } = Select;

// Filters live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  organization: { type: "string" },
  employee: { type: "string" },
  month: { type: "string" }, // YYYY-MM
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 10 },
};

// Ids come back from the URL as strings; match them to the option value.
const matchId = (list, raw) => (raw ? (list.find((item) => String(item.id) === raw)?.id ?? raw) : null);

const PayrollHistory = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });
  // Typed text stays local; the debounced value goes to the URL
  const [searchText, setSearchText] = useState(filters.search || "");
  useEffect(() => setSearchText(filters.search || ""), [filters.search]);
  const selectedMonth = useMemo(() => (filters.month ? dayjs(`${filters.month}-01`) : null), [filters.month]);
  const setSelectedMonth = (date) => setFilters({ month: date ? date.format("YYYY-MM") : null, page: 1 });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Filter states
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const selectedOrganization = matchId(organizations, filters.organization);
  const setSelectedOrganization = urlSetter("organization");
  const selectedEmployee = matchId(employees, filters.employee);
  const setSelectedEmployee = urlSetter("employee");
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Bank transaction states
  const [bankTxnModalVisible, setBankTxnModalVisible] = useState(false);
  const [selectedPayrollForBankTxn, setSelectedPayrollForBankTxn] = useState(null);
  const [selectedBankTxn, setSelectedBankTxn] = useState(null);


  // Load organizations on mount
  useEffect(() => {
    loadOrganizationsList();
    loadEmployeesList();
  }, []);

  const loadOrganizationsList = async () => {
    setLoadingOrganizations(true);
    try {
      const response = await adminactions.loadOrganizations();
      if (response && Array.isArray(response)) {
        setOrganizations(response);
      } else if (response && response.results) {
        setOrganizations(response.results);
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const loadEmployeesList = async () => {
    setLoadingEmployees(true);
    try {
      const response = await adminactions.loadAllEmployees();
      if (response && response.data) {
        const employeeList = response.data.results || response.data || [];
        setEmployees(Array.isArray(employeeList) ? employeeList : []);
      }
    } catch (error) {
      console.error("Error loading employees:", error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Bank transaction functions
  const handleOpenBankTxnModal = (record) => {
    setSelectedPayrollForBankTxn(record);
    setSelectedBankTxn(record.bank_transaction_data || null);
    setBankTxnModalVisible(true);
  };

  const handleBankTxnSelect = async (txnId, txnData) => {
    if (!selectedPayrollForBankTxn) return;

    try {
      await axios.patch(`/payroll/payroll-histories/${selectedPayrollForBankTxn.id}/`, {
        bank_transaction: txnId
      });
      if (txnId) {
        message.success('Bank transaction linked successfully');
      } else {
        message.success('Bank transaction unlinked successfully');
      }
      setSelectedBankTxn(txnData);
      fetchData(pagination.current);
    } catch (error) {
      console.error('Error linking/unlinking bank transaction:', error);
      message.error(txnId ? 'Failed to link bank transaction' : 'Failed to unlink bank transaction');
    }
  };

  const columns = [
    {
      title: "Organization",
      dataIndex: "organization_name",
      key: "organization_name",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      fixed: "left",
      width: 150,
    },
    {
      title: "Employee Code",
      dataIndex: "employee_code",
      key: "employee_code",
      width: 120,
    },
    {
      title: "Month",
      dataIndex: "month_year",
      key: "month_year",
      width: 100,
      render: (text) => {
        if (!text) return "-";
        return dayjs(text, "YYYY-MM").format("MMM YYYY");
      },
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      width: 120,
      render: (text) => text || "-",
    },
    {
      title: "Working Days",
      dataIndex: "total_working_days",
      key: "total_working_days",
      width: 100,
      align: "right",
      render: (val) => val || "-",
    },
    {
      title: "Effective Days",
      dataIndex: "effective_days",
      key: "effective_days",
      width: 110,
      align: "right",
      render: (val) => val || "-",
    },
    {
      title: "Attendance",
      dataIndex: "is_attendance_considered",
      key: "is_attendance_considered",
      width: 100,
      align: "center",
      render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: "Days Present",
      dataIndex: "days_present",
      key: "days_present",
      width: 100,
      align: "right",
      render: (val, record) => record.is_attendance_considered ? parseFloat(val || 0).toFixed(1) : "-",
    },
    {
      title: "Basic Salary",
      dataIndex: "basic_salary",
      key: "basic_salary",
      width: 120,
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-",
    },
    {
      title: "Gross Salary",
      dataIndex: "gross_salary",
      key: "gross_salary",
      width: 120,
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-",
    },
    {
      title: "Total Deductions",
      dataIndex: "total_deductions",
      key: "total_deductions",
      width: 130,
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-",
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      width: 120,
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-",
    },
    {
      title: "Rounded Off",
      dataIndex: "net_salary_rounded_off",
      key: "net_salary_rounded_off",
      width: 120,
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN')}` : "-",
    },
    {
      title: "Bank Txn",
      dataIndex: "bank_transaction",
      key: "bank_transaction",
      width: 120,
      align: "center",
      render: (bank_transaction, record) => (
        <Button
          type={bank_transaction ? "link" : "dashed"}
          size="small"
          icon={bank_transaction ? <BankOutlined style={{ color: '#52c41a' }} /> : <LinkOutlined />}
          onClick={() => handleOpenBankTxnModal(record)}
        >
          {bank_transaction ? 'Linked' : 'Link'}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status, record) => {
        const colorMap = {
          GENERATED: "blue",
          APPROVED: "green",
          PAID: "purple",
          CANCELLED: "red",
        };
        const statusOptions = [
          { key: 'GENERATED', label: 'Generated' },
          { key: 'APPROVED', label: 'Approved' },
          { key: 'PAID', label: 'Paid' },
          { key: 'CANCELLED', label: 'Cancelled' },
        ];
        const menuItems = statusOptions
          .filter(opt => opt.key !== status)
          .map(opt => ({
            key: opt.key,
            label: opt.label,
            onClick: () => handleStatusChange(record.id, opt.key),
          }));
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Tag
              color={colorMap[status] || "default"}
              style={{ cursor: 'pointer' }}
            >
              {status || "GENERATED"} <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
            </Tag>
          </Dropdown>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Details"
          />
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPayslip(record)}
            title="Download Payslip"
          />
          <Popconfirm
            title="Are you sure you want to delete this payroll record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = 1, search = filters.search || "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);

      // Apply filters only when selected
      if (selectedOrganization) {
        params.append('organization', selectedOrganization);
      }
      if (selectedMonth) {
        params.append('month_year', selectedMonth.format("YYYY-MM"));
      }
      if (selectedEmployee) {
        params.append('employee', selectedEmployee);
      }
      if (search) {
        params.append('search', search);
      }

      const url = `/payroll/payroll-histories/?${params.toString()}`;
      console.log('Fetching payroll history:', url);

      const response = await axios.get(url);

      if (response.status === 200) {
        const results = response.data.results || response.data || [];
        console.log('Payroll history results:', results);
        setData(Array.isArray(results) ? results : []);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: response.data.count || (Array.isArray(results) ? results.length : 0),
        });
      }
    } catch (error) {
      console.error("Error fetching payroll history:", error);
      message.error("Failed to fetch payroll history");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilters({ search: value, page: 1 });
    }, 500),
    [setFilters]
  );

  useEffect(() => {
    fetchData(filters.page);
  }, [filters.organization, filters.month, filters.employee, filters.search, filters.page, pageSize]);

  const handleTableChange = (paginationConfig) => {
    // A new page size starts again from page 1
    const sizeChanged = paginationConfig.pageSize !== pageSize;
    setFilters({ page: sizeChanged ? 1 : paginationConfig.current, pageSize: paginationConfig.pageSize });
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleDelete = async (id) => {
    const success = await adminactions.deletePayrollHistory(id);
    if (success) {
      fetchData(pagination.current);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const result = await adminactions.updatePayrollHistoryStatus(id, newStatus);
    if (result) {
      fetchData(pagination.current);
    }
  };

  const handleDownloadPayslip = async (record) => {
    try {
      message.loading({ content: 'Generating payslip...', key: 'download' });
      const response = await axios.get(`/payroll/payroll-histories/${record.id}/download_payslip/`, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthYear = record.month_year.replace('-', '_');
      link.setAttribute('download', `payslip_${record.employee_code}_${monthYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Payslip downloaded successfully!', key: 'download' });
    } catch (error) {
      console.error("Error downloading payslip:", error);
      message.error({ content: 'Failed to download payslip', key: 'download' });
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const handleOrganizationChange = (value) => {
    setSelectedOrganization(value);
  };

  const handleEmployeeChange = (value) => {
    setSelectedEmployee(value);
  };

  const handleClearFilters = () => {
    debouncedSearch.cancel();
    setSearchText("");
    setFilters({ organization: null, month: null, employee: null, search: null, page: 1 });
  };

  const handleRefresh = () => {
    fetchData(1);
  };

  const renderEarningsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#1890ff' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDeductionsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#cf1322' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };


  const hasActiveFilters = selectedOrganization || selectedMonth || selectedEmployee || searchText;

  return (
    <div style={{ padding: 24 }}>
      <style>{`.payroll-history-table .ant-table-thead > tr > th { white-space: nowrap; }`}</style>
      <Card
        title={
          <Space>
            <span>Payroll History</span>
            {hasActiveFilters && (
              <Tag color="blue" icon={<FilterOutlined />}>
                Filters Applied
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} aria-label="Refresh" />
            </Tooltip>
            {hasActiveFilters && (
              <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Space>
        }
      >
        {/* Filter Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6} lg={5}>
            <Select
              placeholder="Select Organization"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={selectedOrganization}
              onChange={handleOrganizationChange}
              loading={loadingOrganizations}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {organizations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5}>
            <Select
              placeholder="Select Employee"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              loading={loadingEmployees}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {employees.map((emp) => (
                <Option key={emp.id} value={emp.id}>
                  {`${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.employee_id}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <DatePicker
              picker="month"
              placeholder="Select Month"
              onChange={handleMonthChange}
              value={selectedMonth}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Input
              placeholder="Search employee, code, department, org..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              style={{ width: '100%' }}
              allowClear
              onClear={() => { debouncedSearch.cancel(); setSearchText(""); setFilters({ search: null, page: 1 }); }}
            />
          </Col>
        </Row>

        <Table
          className="payroll-history-table"
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{ ...pagination, showSizeChanger: true }}
          onChange={handleTableChange}
          scroll={{ x: 2050 }}
          size="small"
        />
      </Card>

      <Modal
        title={`Payroll Details - ${selectedRecord?.employee_name || ''}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedRecord && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p><strong>Employee Name:</strong> {selectedRecord.employee_name}</p>
                <p><strong>Employee Code:</strong> {selectedRecord.employee_code}</p>
                <p><strong>Department:</strong> {selectedRecord.department_name || '-'}</p>
                <p><strong>Designation:</strong> {selectedRecord.designation || '-'}</p>
                <p><strong>Organization:</strong> {selectedRecord.organization_name || '-'}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p><strong>Month:</strong> {dayjs(selectedRecord.month_year, "YYYY-MM").format("MMMM YYYY")}</p>
                <p><strong>Working Days:</strong> {selectedRecord.total_working_days || '-'}</p>
                <p><strong>Effective Days:</strong> {selectedRecord.effective_days || '-'}</p>
                <p><strong>Days Present:</strong> {selectedRecord.is_attendance_considered ? parseFloat(selectedRecord.days_present || 0).toFixed(1) : '-'}</p>
                <p><strong>Attendance Considered:</strong> {selectedRecord.is_attendance_considered ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>}</p>
                <p><strong>Status:</strong> <Tag color={selectedRecord.status === 'PAID' ? 'purple' : selectedRecord.status === 'APPROVED' ? 'green' : 'blue'}>{selectedRecord.status}</Tag></p>
              </div>
            </div>

            <Divider />

            <div style={{ display: 'flex', gap: 16 }}>
              <Card size="small" title="Earnings Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#f6ffed' }}>
                {renderEarningsBreakdown(selectedRecord.earnings_breakdown, selectedRecord.ytd_earnings_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Earnings:</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedRecord.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedRecord.ytd_total_earnings !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedRecord.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>

              <Card size="small" title="Deductions Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#fff2e8' }}>
                {renderDeductionsBreakdown(selectedRecord.deductions_breakdown, selectedRecord.ytd_deductions_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Deductions:</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedRecord.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedRecord.ytd_total_deductions !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedRecord.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>
            </div>

            <Divider />

            <Card size="small" style={{ backgroundColor: '#f0f9ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
                <span style={{ flex: 2 }}>Summary</span>
                <span style={{ flex: 1, textAlign: 'right' }}>This Month</span>
                {selectedRecord.ytd_net_salary !== undefined && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Basic Salary:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedRecord.basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedRecord.ytd_basic_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedRecord.ytd_basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Earnings:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedRecord.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedRecord.ytd_total_earnings !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedRecord.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Deductions:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedRecord.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedRecord.ytd_total_deductions !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedRecord.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold' }}>
                <span style={{ flex: 2 }}>Net Salary:</span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedRecord.net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedRecord.ytd_net_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#52c41a' }}>₹{parseFloat(selectedRecord.ytd_net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginTop: 4 }}>
                <span style={{ flex: 2 }}><strong>Rounded Off:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff', fontWeight: 'bold' }}>₹{Math.round(parseFloat(selectedRecord.net_salary || 0)).toLocaleString('en-IN')}</span>
                {selectedRecord.ytd_net_salary !== undefined && <span style={{ flex: 1 }}></span>}
              </div>
            </Card>

            {selectedRecord.generated_at && (
              <div style={{ marginTop: 12, textAlign: 'right', color: '#888' }}>
                Generated: {dayjs(selectedRecord.generated_at).format("DD MMM YYYY, hh:mm A")}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Bank Transaction Link Modal */}
      <BankTransactionSelectModal
        visible={bankTxnModalVisible}
        onClose={() => {
          setBankTxnModalVisible(false);
          setSelectedPayrollForBankTxn(null);
        }}
        onSelect={handleBankTxnSelect}
        selectedTransactionId={selectedBankTxn?.id}
        title={`Link Bank Transaction - ${selectedPayrollForBankTxn?.employee_name || ''}`}
      />
    </div>
  );
};

export default PayrollHistory;
